import React from "react";

import { useTranslation } from "utils";

import { Typography } from "components";

import { HorizontalScrollFade } from "pages/app/scenario-analysis/components/HorizontalScrollFade";
import { useDifferenceAmount } from "pages/app/scenario-analysis/hooks/useDifferenceAmount";
import { useYearLabel } from "pages/app/scenario-analysis/hooks/useYearLabel";
import { DifferenceDatum } from "pages/app/scenario-analysis/utils/differenceView";
import { shouldUseOutcomeYearScroll } from "pages/app/scenario-analysis/utils/forecast";

type DifferenceYearTableProps = {
  series: DifferenceDatum[];
  comparisonTitle: string;
  baselineTitle: string;
  lineColor: string;
  dataTestId?: string;
};

/** Year-by-year running difference for the one scenario the chart above plots. */
export const DifferenceYearTable = ({ series, comparisonTitle, baselineTitle, lineColor, dataTestId = "difference-year-table" }: DifferenceYearTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const differenceAmount = useDifferenceAmount();
  const { formatYearShort } = useYearLabel();

  const useYearScroll = shouldUseOutcomeYearScroll(series.length);

  const table = (
    <div className="outcome-comparison-table-wrapper outcome-comparison-table-wrapper--impact">
      <table className="outcome-comparison-table outcome-comparison-table--annual-yield" data-testid={`${dataTestId}-table`}>
        <thead>
          <tr>
            <th scope="col" className="outcome-comparison-table-head outcome-comparison-table-head-sticky">
              {t("outcomeComparison.difference.tableHeader", { baseline: baselineTitle })}
            </th>
            {series.map((row) => (
              <th key={row.yearNum} scope="col" className="outcome-comparison-table-head">
                {formatYearShort(row.yearNum)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="outcome-comparison-table-row outcome-comparison-table-row--parent" data-testid={`${dataTestId}-row`}>
            <th scope="row" className="outcome-comparison-table-name outcome-comparison-table-name-sticky">
              <span className="outcome-comparison-table-name-dot" style={{ backgroundColor: lineColor }} aria-hidden />
              <Typography variant="paragraph-m" className="outcome-comparison-table-title">
                {comparisonTitle}
              </Typography>
            </th>
            {series.map((row) => (
              <td key={row.yearNum} className="outcome-comparison-table-cell">
                <div className="outcome-comparison-table-cell-inner">
                  {/* Only losses are coloured, on the raw gap — the same value the chart shades and the
                      Breaks even card counts. A gap too small to round to €100 prints its exact figure,
                      so a red cell always carries a number that earns the red. */}
                  <span
                    className={`outcome-comparison-table-cm ${row.difference < 0 ? "outcome-comparison-table-change-negative" : ""}`}
                    data-testid={`${dataTestId}-y${row.yearNum}`}
                  >
                    {differenceAmount(row.difference)}
                  </span>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="outcome-comparison-impact" data-testid={dataTestId}>
      {useYearScroll ? (
        // One row and no sticky right column, so the viewport runs the full card width and only scrolls once the years overflow it.
        <HorizontalScrollFade dataTestId={`${dataTestId}-scroll`} enabled chromeOffsetLeft={11}>
          {table}
        </HorizontalScrollFade>
      ) : (
        table
      )}
    </div>
  );
};

export default DifferenceYearTable;
